<template>
  <div class="plat-work-board mp-page mp-page--wide" :class="{ ready: loaded }">
    <header class="board-top">
      <BoardCrumb
        level="item"
        :platform="cfg.key"
        :group-id="fromGroupId"
        :group-name="fromGroupName"
        :range="route.query.range || 7"
        :account-id="accountId"
        :account-label="account.display_name || accountId"
        :item-label="work.title || cfg.itemLabel"
      />
      <div class="board-actions">
        <el-button type="primary" plain @click="backToAccount">返回账号看板</el-button>
        <el-button :loading="loading" @click="loadWork(false)">刷新本地</el-button>
        <el-button type="primary" plain :loading="syncing" @click="loadWork(true)">同步平台</el-button>
      </div>
    </header>

    <el-alert v-if="error" type="error" :closable="false" show-icon :title="error" class="board-alert" />

    <section class="work-hero mp-panel" v-loading="(loading || syncing) && !loaded">
      <img v-if="work.cover_url" class="work-cover" :src="coverSrc(work.cover_url)" alt="" />
      <div class="work-hero-text">
        <h1>{{ work.title || cfg.itemLabel }}</h1>
        <p>
          <span>{{ formatBeijingDateTime(work.published_at || work.post_time) }}</span>
          <span class="mono"> · {{ itemId }}</span>
          <span v-if="dataSource"> · {{ dataSource === 'local' ? '本地' : '实时' }}</span>
        </p>
      </div>
    </section>

    <section class="mp-panel">
      <div class="kpi-grid">
        <div v-for="k in kpiCards" :key="k.label" class="kpi">
          <span>{{ k.label }}</span>
          <strong>{{ k.value }}</strong>
        </div>
      </div>
    </section>

    <section v-if="work.content || work.desc" class="mp-panel">
      <h3>正文</h3>
      <p class="body-text">{{ work.content || work.desc }}</p>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { publishV2Api } from '../api'
import BoardCrumb from '../components/BoardCrumb.vue'
import { resolveGroupId, resolveGroupName, accountBoardLink } from '../utils/boardNav'
import { formatBeijingDateTime } from '../utils/dateTime'
import { PLATFORM_WORK_BOARD } from '../utils/platformBoardConfig'

const props = defineProps({
  platform: { type: String, required: true },
})

const cfg = computed(() => PLATFORM_WORK_BOARD[props.platform])
const route = useRoute()
const router = useRouter()
const accountId = computed(() => String(route.params.accountId || ''))
const itemId = computed(() => String(route.params[cfg.value.paramKey] || route.params.noteId || route.params.itemId || ''))
const fromGroupId = computed(() => resolveGroupId(route.query))
const fromGroupName = computed(() => resolveGroupName(route.query, fromGroupId.value))
const loading = ref(false)
const syncing = ref(false)
const loaded = ref(false)
const error = ref('')
const payload = ref({})

const account = computed(() => payload.value.account || {})
const work = computed(() => payload.value.note || payload.value.work || payload.value.item || payload.value || {})
const metrics = computed(() => payload.value.metrics || work.value.metrics || work.value)
const dataSource = computed(() => payload.value.data_source || '')

const formatNum = (v) => {
  const n = Number(v || 0)
  if (!Number.isFinite(n)) return '0'
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(1)}万`
  return String(Math.round(n))
}
const coverSrc = (url) => (/^https?:\/\//i.test(String(url || '')) ? publishV2Api.coverProxyUrl(url) : url)

const kpiCards = computed(() => {
  const m = metrics.value || {}
  const pairs = [
    ['观看 / 播放', m.view_count ?? m.read_count ?? m.play ?? m.views ?? m.vv],
    ['点赞', m.like_count ?? m.likes ?? m.like],
    ['评论', m.comment_count ?? m.comments ?? m.comment],
    ['收藏', m.collect_count ?? m.collects ?? m.collect],
    ['分享', m.share_count ?? m.shares ?? m.share],
    ['涨粉', m.increase_fans_count ?? m.rise_fans],
  ]
  return pairs
    .filter(([, v]) => v != null && v !== '')
    .map(([label, value]) => ({ label, value: formatNum(value) }))
})

async function loadWork(sync = false) {
  if (!accountId.value || !itemId.value) return
  if (sync) syncing.value = true
  else loading.value = true
  error.value = ''
  try {
    const data = await cfg.value.fetchWork(accountId.value, itemId.value, { sync })
    payload.value = cfg.value.normalize ? cfg.value.normalize(data || {}) : (data || {})
    loaded.value = true
  } catch (e) {
    error.value = e?.response?.data?.detail || e?.message || '加载失败'
  } finally {
    loading.value = false
    syncing.value = false
  }
}

function backToAccount() {
  router.push(accountBoardLink(cfg.value.key, accountId.value, route))
}

onMounted(() => loadWork(false))
</script>

<style scoped>
.plat-work-board { min-height: calc(100vh - 48px); padding: 4px 0 32px; }
.board-top { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.board-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.board-alert { margin-bottom: 12px; }
.work-hero { display: flex; gap: 16px; align-items: center; padding: 16px; }
.work-cover { width: 120px; height: 120px; object-fit: cover; border-radius: 12px; background: #f0f2f6; flex-shrink: 0; }
.work-hero-text h1 { margin: 0; font-size: 22px; }
.work-hero-text p { margin: 8px 0 0; color: var(--mp-text-muted); font-size: 13px; }
.mono { font-family: ui-monospace, monospace; }
.kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; padding: 8px 0; }
.kpi { background: #f6f8fb; border-radius: 12px; padding: 12px 14px; }
.kpi span { display: block; font-size: 12px; color: var(--mp-text-muted); }
.kpi strong { font-size: 22px; }
.body-text { white-space: pre-wrap; line-height: 1.7; color: var(--mp-text-secondary); }
h3 { margin: 0 0 10px; font-size: 15px; }

@media (max-width: 768px) {
  .board-top { flex-direction: column; align-items: stretch; }
  .board-actions .el-button { flex: 1; }
  .work-hero { flex-direction: column; align-items: flex-start; }
  .work-cover { width: 100%; height: auto; aspect-ratio: 1; max-width: 200px; }
  .work-hero-text h1 { font-size: 18px; }
  .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
